// AccountSubscription.tsx
import React, { useCallback, useEffect, useState } from 'react';
import type { TourProps } from 'antd';
import { resolveFrontendApiBase } from '../utils/apiBase';
import { Typography, Spin } from 'antd';
import axios from '../utils/accountApi';
import { getAuthHeaders } from '../utils/authHeaders';
import { useAccountContext } from '../Account';
import { CustomPricing } from '../CustomPricing';
import AppTour, { renderAppTourPanel } from '../tours/AppTour';
import { tourTarget, useGuidedTour } from '../tours/useGuidedTour';
import { ACCOUNT_SUBSCRIPTION_TOUR_EVENT, ACCOUNT_SUBSCRIPTION_TOUR_STORAGE_KEY } from './accountTourEvents';
import '../CustomPricing.css';

const { Title, Text } = Typography;

const API_BASE = resolveFrontendApiBase();
const ON_BREAK_PRODUCT_ID = 'prod_UPpK4JaS2kr0NM';
const ON_BREAK_PRICE_ID = 'price_1TUWTvEj6HHcTsCPlloksVKd';

const isOnBreakIdentifier = (value?: string | null): boolean =>
  value === ON_BREAK_PRODUCT_ID || value === ON_BREAK_PRICE_ID || value === 'on_break';

export default function AccountSubscription(): JSX.Element {
  const { isAuth, usersSharedState, showLoginModal } = useAccountContext();

  const [currentPlanTier, setCurrentPlanTier] = useState<number>(0);
  const [currentPlanPriceId, setCurrentPlanPriceId] = useState<string | undefined>(undefined);
  const [loadingTier, setLoadingTier] = useState<boolean>(true);
  const [currentPlanIsLegacy, setCurrentPlanIsLegacy] = useState<boolean>(false);
  const [pendingChange, setPendingChange] = useState<null | {
    targetPriceId: string;
    targetProductId?: string;
    effectiveAt?: number;
    action?: 'upgrade' | 'downgrade';
  }>(null);
  const [subscriptionTourPendingStart, setSubscriptionTourPendingStart] = useState(false);
  const subscriptionTour = useGuidedTour({
    userId: usersSharedState?.userID,
    apiBase: API_BASE,
    flag: 'accountSubscriptionTourSeen',
    enabled: isAuth,
    autoStart: true,
    ready: !loadingTier,
  });
  const startSubscriptionTour = subscriptionTour.start;
  const subscriptionTourSteps = React.useMemo<TourProps['steps']>(() => ([
    {
      title: 'Review your plan workspace',
      description: renderAppTourPanel('This page compares subscription plans and shows the plan state used when you change or renew your subscription.'),
      target: tourTarget('account-subscription-header'),
    },
    {
      title: 'Compare subscription options',
      description: renderAppTourPanel('Plan cards show recurring credits, price, cadence, and upgrade or downgrade actions.'),
      target: tourTarget('account-subscription-pricing', 'account-subscription-header'),
    },
    {
      title: 'Confirm checkout changes',
      description: renderAppTourPanel('Plan buttons hand off to checkout or schedule account changes depending on your current subscription.'),
      target: tourTarget('account-subscription-pricing'),
    },
  ]), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const pending = window.sessionStorage.getItem(ACCOUNT_SUBSCRIPTION_TOUR_STORAGE_KEY);
    if (pending) {
      window.sessionStorage.removeItem(ACCOUNT_SUBSCRIPTION_TOUR_STORAGE_KEY);
      setSubscriptionTourPendingStart(true);
    }
    const handleTourRequest = () => {
      setSubscriptionTourPendingStart(true);
    };
    window.addEventListener(ACCOUNT_SUBSCRIPTION_TOUR_EVENT, handleTourRequest as EventListener);
    return () => {
      window.removeEventListener(ACCOUNT_SUBSCRIPTION_TOUR_EVENT, handleTourRequest as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!subscriptionTourPendingStart || loadingTier) return;
    startSubscriptionTour();
    setSubscriptionTourPendingStart(false);
  }, [loadingTier, startSubscriptionTour, subscriptionTourPendingStart]);

  const isFuture = (unix?: number) => typeof unix === 'number' && unix * 1000 > Date.now();

  // Fetch current plan tier and any scheduled change so CustomPricing can display the correct state
  const fetchTierAndSchedule = useCallback(async () => {
    if (!isAuth || !usersSharedState?.userID) return;
    setLoadingTier(true);
    const isRenewableStatus = (s: any) => {
      const st = String(s?.status || '').toLowerCase();
      if (st === 'active' || st === 'trialing' || st === 'past_due') return true;
      if ((st === 'canceled' || st === 'cancelled') && isFuture(s?.cancel_at)) return true;
      return false;
    };
    const resolveTierFromSubscriptions = async (): Promise<boolean> => {
      try {
        const subsResp = await axios.get(`${API_BASE}/get-user-subscriptions`, {
          params: { userID: usersSharedState.userID }
        });
        const subs: any[] = Array.isArray(subsResp?.data?.subscriptions) ? subsResp.data.subscriptions : [];

        const renewable = subs.find(isRenewableStatus);
        if (!renewable) {
          setCurrentPlanPriceId(undefined);
          return false;
        }

        const productId: string | undefined =
          renewable.items?.[0]?.price?.product ??
          renewable.plan_product_id ??
          renewable.product_id;

        const priceId: string | undefined =
          renewable.items?.[0]?.price?.id ??
          renewable.price_id;

        const idForPlanLookup = priceId ?? productId;
        if (!idForPlanLookup) return false;

        const headers = await getAuthHeaders();
        const pr = await axios.get(`${API_BASE}/get_plan_details/${idForPlanLookup}`, {
          params: { ...(priceId ? { priceId } : {}), userID: usersSharedState.userID, surface: 'account' },
          headers,
        });
        const t = pr?.data?.tier || 0;
        setCurrentPlanTier(t);
        setCurrentPlanIsLegacy(Boolean(pr?.data?.isLegacy));
        if (priceId) {
          setCurrentPlanPriceId(priceId);
        }
        return t > 0;
      } catch {
        setCurrentPlanPriceId(undefined);
        return false;
      }
    };

    const applySubscriptionDetails = async (): Promise<boolean> => {
      try {
        const headers = await getAuthHeaders();
        const detailsResp = await axios.get(`${API_BASE}/get-subscription-details`, {
          params: { userID: usersSharedState.userID },
          headers,
        });
        const d = detailsResp.data || {};
        const livePriceId = typeof d.plan_id === 'string' && d.plan_id ? d.plan_id : undefined;
        const liveProductId = typeof d.product_id === 'string' && d.product_id ? d.product_id : undefined;
        const hasLiveSubscription = Boolean(
          d.status &&
          d.status !== 'cancelled' &&
          (livePriceId || liveProductId)
        );

        if (d.scheduled_target_price_id) {
          setPendingChange({
            targetPriceId: d.scheduled_target_price_id,
            targetProductId: d.scheduled_target_product_id,
            effectiveAt: d.scheduled_effective_date,
            action: d.scheduled_action,
          });
        } else {
          setPendingChange(null);
        }

        if (!hasLiveSubscription) {
          return false;
        }

        if (livePriceId) {
          setCurrentPlanPriceId(livePriceId);
        }

        if (d.is_on_break || isOnBreakIdentifier(livePriceId) || isOnBreakIdentifier(liveProductId)) {
          setCurrentPlanTier(0);
          setCurrentPlanIsLegacy(false);
          return true;
        }

        const lookupId = livePriceId || liveProductId;
        if (lookupId) {
          try {
            const planResp = await axios.get(`${API_BASE}/get_plan_details/${lookupId}`, {
              params: { ...(livePriceId ? { priceId: livePriceId } : {}), userID: usersSharedState.userID, surface: 'account' },
              headers,
            });
            setCurrentPlanTier(planResp?.data?.tier || 0);
            setCurrentPlanIsLegacy(Boolean(planResp?.data?.isLegacy));
          } catch {
            setCurrentPlanTier(0);
            setCurrentPlanIsLegacy(false);
          }
        }
        return true;
      } catch {
        setPendingChange(null);
        return false;
      }
    };

    try {
      const liveDetailsResolved = await applySubscriptionDetails();

      // Current plan tier (existing endpoints)
      const creditResponse = await axios.get(
        `${API_BASE}/check_credit_balance/`,
        { params: { userID: usersSharedState.userID } }
      );
      const planProductID = typeof creditResponse.data.planProductID === 'string' ? creditResponse.data.planProductID : undefined;
      const planPriceID = typeof creditResponse.data.planPriceID === 'string' ? creditResponse.data.planPriceID : undefined;
      const lookupField = typeof creditResponse.data.planLookupID === 'string' ? creditResponse.data.planLookupID : undefined;
      const planIDRaw: any = creditResponse.data.planID;
      const priceIDRaw: any = creditResponse.data.priceID;
      const fallbackPriceID: string | undefined =
        typeof priceIDRaw === 'string' && priceIDRaw
          ? priceIDRaw
          : undefined;
      const fallbackPlanID: string | undefined =
        typeof planIDRaw === 'string' && planIDRaw
          ? planIDRaw
          : (typeof planIDRaw === 'number' && planIDRaw !== 0 ? String(planIDRaw) : undefined);
      const lookupId = planPriceID || fallbackPriceID || lookupField || fallbackPlanID || planProductID;
      const priceID: string | undefined = planPriceID || fallbackPriceID;

      let tierResolved = liveDetailsResolved;
      try {
        if (liveDetailsResolved) {
          throw new Error('live subscription details already resolved');
        }
        if (!lookupId) {
          throw new Error('missing plan lookup id');
        }
        const headers = await getAuthHeaders();
        const planResponse = await axios.get(`${API_BASE}/get_plan_details/${lookupId}`, {
          params: { ...(priceID ? { priceId: priceID } : {}), userID: usersSharedState.userID, surface: 'account' },
          headers,
        });
        const t = planResponse?.data?.tier || 0;
        setCurrentPlanTier(t);
        setCurrentPlanIsLegacy(Boolean(planResponse?.data?.isLegacy));
        if (priceID) {
          setCurrentPlanPriceId(priceID);
        } else if (planResponse?.data?.stripePriceID) {
          setCurrentPlanPriceId(planResponse.data.stripePriceID);
        } else if (planProductID) {
          setCurrentPlanPriceId(undefined);
        }
        tierResolved = Boolean(priceID || planProductID || t > 0);
      } catch {
        if (!liveDetailsResolved) {
          tierResolved = false;
          setCurrentPlanPriceId(undefined);
        }
      }

      if (!tierResolved) {
        const resolved = await resolveTierFromSubscriptions();
        if (!resolved) {
          setCurrentPlanIsLegacy(false);
        }
      }

    } catch (e) {
      console.error('Failed to fetch current plan tier:', e);
      const ok = await resolveTierFromSubscriptions();
      if (!ok) {
        setCurrentPlanTier(0);
        setCurrentPlanIsLegacy(false);
        setCurrentPlanPriceId(undefined);
      }
      setPendingChange(null);
    } finally {
      setLoadingTier(false);
    }
  }, [isAuth, usersSharedState]);

  useEffect(() => {
    fetchTierAndSchedule();
  }, [fetchTierAndSchedule]);

  // Gate unauthenticated state similarly to AccountBilling
  if (!isAuth) {
    return (
      <div className="account-subscription-shell">
        <div className="pricing-auth-empty">
          <Text>Please sign in to manage your subscription.</Text>
        </div>
      </div>
    );
  }

  return (
    <section className="account-subscription-shell">
      <div className="account-subscription-header" data-tour-id="account-subscription-header">
        <Title level={4}>Subscription Plans</Title>
        <p>Compare your options, manage scheduled changes, and update your plan.</p>
      </div>
      <div className="account-subscription-surface" data-tour-id="account-subscription-pricing">
      {loadingTier ? (
        <div className="pricing-loading-state">
          <Spin />
        </div>
      ) : (
        <CustomPricing
          userID={usersSharedState.userID!}
          email={usersSharedState.email}
          isForeverCredits={false}
          currentPlanPriceId={currentPlanPriceId}
          currentPlanIsLegacy={currentPlanIsLegacy}
          isAuth={isAuth}
          showLoginModal={showLoginModal}
          onSuccess={fetchTierAndSchedule}
          pendingChange={pendingChange || undefined}      
          includeSubscriberOnlyPlans={true}
          pricingSurface="account"
        />
      )}
      </div>
      <AppTour
        open={subscriptionTour.open}
        steps={subscriptionTourSteps}
        current={subscriptionTour.current}
        onChange={subscriptionTour.setCurrent}
        onClose={subscriptionTour.close}
        onFinish={subscriptionTour.close}
      />
    </section>
  );
}
